// src/providers/PeerManager.jsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";

const PeerManagerContext = createContext(null);
export const usePeerManager = () => useContext(PeerManagerContext);

const ICE_CONFIG = {
  iceServers: [
    {
      urls: [
        "stun:stun.l.google.com:19302",
        "stun:global.stun.twilio.com:3478",
      ],
    },
    {
      urls: [
        "turn:stun.evan-brass.net",
        "turn:stun.evan-brass.net?transport=tcp",
        "turn:stun.evan-brass.net?transport=udp",
        "stun:stun.evan-brass.net",
      ],
      username: "guest",
      credential: "password",
    },
    {
      urls: [
        "turn:turn.pickitforme.com",
        "turn:turn.pickitforme.com?transport=tcp",
        "turn:turn.pickitforme.com?transport=udp",
        "stun:stun.pickitforme.com",
      ],
      username: "user1",
      credential: "password1",
    },
  ],
};

export const PeerManagerProvider = ({ children }) => {
  // authoritative peers map in a ref (so we can mutate synchronously)
  const peersRef = useRef({}); // { [emailId]: RTCPeerConnection }
  const [peersState, _setPeersState] = useState({}); // mirror for re-renders

  const [remoteStreams, setRemoteStreams] = useState({}); // { [emailId]: MediaStream }
  const [localStream, setLocalStream] = useState(null);

  // candidates that arrive before remoteDescription is set
  const pendingCandidatesRef = useRef({}); // { [emailId]: RTCIceCandidate[] }

  const peers = peersState;

  const setPeersSafe = useCallback((updater) => {
    const current = peersRef.current;
    const next =
      typeof updater === "function" ? updater(current) : updater;
    peersRef.current = next;
    _setPeersState(next);
  }, []);

  const attachLocalTracks = useCallback(
    (pc) => {
      if (!localStream) return;

      localStream.getTracks().forEach((track) => {
        const existingSender = pc
          .getSenders()
          .find((s) => s.track && s.track.kind === track.kind);

        if (existingSender) {
          existingSender.replaceTrack(track);
        } else {
          pc.addTrack(track, localStream);
        }
      });
    },
    [localStream]
  );

  const createPeerFor = useCallback(
    (remoteEmailId) => {
      const pc = new RTCPeerConnection(ICE_CONFIG);

      pc.ontrack = (event) => {
        console.log("[PEER-MGR] ontrack from", remoteEmailId, event);
        const stream = event.streams[0] || new MediaStream([event.track]);
        setRemoteStreams((prev) => ({
          ...prev,
          [remoteEmailId]: stream,
        }));
      };

      pc.oniceconnectionstatechange = () => {
        console.log(
          "[PEER-MGR] iceConnectionState",
          remoteEmailId,
          pc.iceConnectionState
        );
  if (pc.iceConnectionState === "failed") {
    console.warn("[PEER-MGR] ICE failed for", remoteEmailId);
  }
      };

      attachLocalTracks(pc);
      return pc;
    },
    [attachLocalTracks]
  );

  const getOrCreatePeer = useCallback(
    (remoteEmailId) => {
      // read from ref synchronously
      let existing = peersRef.current[remoteEmailId];
      if (existing) return existing;

      // create new
      const pc = createPeerFor(remoteEmailId);

      // write back to ref + state
      setPeersSafe((prev) => ({
        ...prev,
        [remoteEmailId]: pc,
      }));

      return pc;
    },
    [createPeerFor, setPeersSafe]
  );

  const setLocalMediaStream = useCallback(
    (stream) => {
      setLocalStream(stream);

      // push to all existing peers
      setPeersSafe((prev) => {
        Object.values(prev).forEach((pc) => {
          stream.getTracks().forEach((track) => {
            const existingSender = pc
              .getSenders()
              .find((s) => s.track && s.track.kind === track.kind);

            if (existingSender) {
              existingSender.replaceTrack(track);
            } else {
              pc.addTrack(track, stream);
            }
          });
        });
        return prev;
      });
    },
    [setPeersSafe]
  );

  const removePeer = useCallback((remoteEmailId) => {
    const current = peersRef.current;
    const pc = current[remoteEmailId];

    if (pc) {
      try {
        // stop only remote tracks (receivers); local tracks belong to localStream
        pc.getReceivers().forEach((r) => {
          if (r.track) {
            try {
              r.track.stop();
            } catch (e) {
              console.warn(
                "[PEER-MGR] error stopping receiver track for",
                remoteEmailId,
                e
              );
            }
          }
        });
        pc.close();
      } catch (e) {
        console.warn("[PEER-MGR] error closing pc for", remoteEmailId, e);
      }
    }

    // remove from peers
    setPeersSafe((prev) => {
      const { [remoteEmailId]: _, ...rest } = prev;
      return rest;
    });

    // remove remote stream
    setRemoteStreams((prev) => {
      const { [remoteEmailId]: _, ...rest } = prev;
      return rest;
    });

    // clear pending candidates
    if (pendingCandidatesRef.current[remoteEmailId]) {
      delete pendingCandidatesRef.current[remoteEmailId];
    }
  }, [setPeersSafe]);

  const resetAllPeers = useCallback(() => {
    // used when *local* user exits
    Object.entries(peersRef.current).forEach(([remoteId, pc]) => {
      try {
        pc.close();
      } catch (e) {
        console.warn("[PEER-MGR] error closing pc for", remoteId, e);
      }
    });
    peersRef.current = {};
    _setPeersState({});
    setRemoteStreams({});
    pendingCandidatesRef.current = {};
  }, []);

  // inside PeerManagerProvider, near the top-level hooks
const flushPendingCandidates = useCallback(
  async (remoteEmailId, pc) => {
    const queue = pendingCandidatesRef.current[remoteEmailId] || [];
    if (!queue.length) return;

    console.log(
      "[PEER-MGR] Flushing",
      queue.length,
      "pending ICE candidates for",
      remoteEmailId
    );

    for (const cand of queue) {
      try {
        await pc.addIceCandidate(cand);
      } catch (e) {
        console.warn(
          "[PEER-MGR] error adding queued candidate for",
          remoteEmailId,
          e
        );
      }
    }

    pendingCandidatesRef.current[remoteEmailId] = [];
  },
  []
);

  // ---------- Signaling helpers ----------

  const createOfferFor = useCallback(
    async (remoteEmailId, onIceCandidate) => {
      const pc = getOrCreatePeer(remoteEmailId);

      // pc is guaranteed non-null now
      pc.onicecandidate = (event) => {
         if (event.candidate) {
    console.log(
      "[ICE]",
      remoteEmailId,
      "type=" + event.candidate.type,
      "cand=" + event.candidate.candidate
    );
  }
        if (event.candidate && onIceCandidate) {
          onIceCandidate(remoteEmailId, event.candidate);
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      return offer;
    },
    [getOrCreatePeer]
  );

const createAnswerFor = useCallback(
  async (remoteEmailId, offer, onIceCandidate) => {
    const pc = getOrCreatePeer(remoteEmailId);

    pc.onicecandidate = (event) => {
      if (event.candidate && onIceCandidate) {
        onIceCandidate(remoteEmailId, event.candidate);
      }
    };

    await pc.setRemoteDescription(new RTCSessionDescription(offer));

    // NEW: flush any candidates that arrived before we had the offer set
    await flushPendingCandidates(remoteEmailId, pc);

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    return answer;
  },
  [getOrCreatePeer, flushPendingCandidates]
);


const setRemoteAnswerFor = useCallback(
  async (remoteEmailId, answer) => {
    const pc = getOrCreatePeer(remoteEmailId);

    if (pc.remoteDescription && pc.remoteDescription.type === "answer") {
      console.log(
        "[PEER-MGR] remote answer already set for",
        remoteEmailId
      );
      return;
    }

    await pc.setRemoteDescription(new RTCSessionDescription(answer));

    // Flush queued candidates now that we’re ready
    await flushPendingCandidates(remoteEmailId, pc);
  },
  [getOrCreatePeer, flushPendingCandidates]
);



  // const setRemoteAnswerFor = useCallback(
  //   async (remoteEmailId, answer) => {
  //     const pc = getOrCreatePeer(remoteEmailId);

  //     if (
  //       pc.remoteDescription &&
  //       pc.remoteDescription.type === "answer"
  //     ) {
  //       console.log(
  //         "[PEER-MGR] remote answer already set for",
  //         remoteEmailId
  //       );
  //       return;
  //     }

  //     await pc.setRemoteDescription(new RTCSessionDescription(answer));

  //     // flush pending candidates
  //     const queue = pendingCandidatesRef.current[remoteEmailId] || [];
  //     if (queue.length) {
  //       console.log(
  //         "[PEER-MGR] Flushing",
  //         queue.length,
  //         "pending ICE candidates for",
  //         remoteEmailId
  //       );
  //       for (const cand of queue) {
  //         try {
  //           await pc.addIceCandidate(cand);
  //         } catch (e) {
  //           console.warn(
  //             "[PEER-MGR] error adding queued candidate for",
  //             remoteEmailId,
  //             e
  //           );
  //         }
  //       }
  //       pendingCandidatesRef.current[remoteEmailId] = [];
  //     }
  //   },
  //   [getOrCreatePeer]
  // );

  const addIceCandidateFor = useCallback(
    async (remoteEmailId, candidate) => {
      const pc = getOrCreatePeer(remoteEmailId);

      if (!pc.remoteDescription || !pc.remoteDescription.type) {
        console.log(
          "[PEER-MGR] Queueing candidate for",
          remoteEmailId,
          "because remoteDescription is null"
        );
        if (!pendingCandidatesRef.current[remoteEmailId]) {
          pendingCandidatesRef.current[remoteEmailId] = [];
        }
        pendingCandidatesRef.current[remoteEmailId].push(
          new RTCIceCandidate(candidate)
        );
        return;
      }

      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.warn(
          "[PEER-MGR] error adding ICE candidate for",
          remoteEmailId,
          e
        );
      }
    },
    [getOrCreatePeer]
  );

  const value = {
    peers,
    remoteStreams,
    localStream,
    setLocalMediaStream,
    removePeer,
    resetAllPeers,
    createOfferFor,
    createAnswerFor,
    setRemoteAnswerFor,
    addIceCandidateFor,
  };

  return (
    <PeerManagerContext.Provider value={value}>
      {children}
    </PeerManagerContext.Provider>
  );
};
